import { CommandPalette } from "@app/components/command_palette/CommandPalette";
import { DEV_MODE_ACTIVE } from "@app/components/dev/devModeConstants";
import { useDesktopNavigation } from "@app/components/navigation/DesktopNavigationContext";
import { Navigation } from "@app/components/navigation/Navigation";
import { TopBanners } from "@app/components/navigation/TopBanners";
import { useAppLayout } from "@app/components/sparkle/AppLayoutContext";
import { AppLayoutTitle } from "@app/components/sparkle/AppLayoutTitle";
import { useAppKeyboardShortcuts } from "@app/hooks/useAppKeyboardShortcuts";
import { useDocumentScrollMode } from "@app/hooks/useDocumentScrollMode";
import { useDocumentTitle } from "@app/hooks/useDocumentTitle";
import { useHashParam } from "@app/hooks/useHashParams";
import { useAuth, useWorkspace } from "@app/lib/auth/AuthContext";
import { PRODUCT_NAME } from "@app/lib/compta/branding";
import { modeIntegre } from "@app/lib/compta/mode_integre";
import { useNavigationDeLApp } from "@app/lib/compta/navigation_app";
import { MOBILE_DOCUMENT_SCROLL_CLASSES } from "@app/lib/documentScrollLayoutClasses";
import { useIsMobile } from "@app/lib/swr/useIsMobile";
import { FULL_SCREEN_HASH_PARAM } from "@app/types/conversation_side_panel";
import { cn } from "@dust-tt/sparkle";
import type React from "react";
import { lazy, Suspense } from "react";

// Lazy-load the dev panel only when dev mode is active.
// The module is never fetched/parsed/executed when dev mode is off.
const DevFeatureFlagPanel = DEV_MODE_ACTIVE
  ? lazy(() =>
      // Dynamic import is necessary here: the dev panel must not be bundled
      // in the main chunk — it should only load when dev mode is active.
      import("@app/components/dev/DevFeatureFlagPanel").then((m) => ({
        default: m.DevFeatureFlagPanel,
      }))
    )
  : null;

interface AppContentLayoutProps {
  children: React.ReactNode;
}

interface AppContentInnerWrapperProps {
  isNavigationBarOpen: boolean;
  isMobile: boolean;
  isFullScreen: boolean;
  /** Maquette de la coquille : le chat porte la coquille, rectangle arrondi compris. */
  coquille?: boolean;
  children: React.ReactNode;
}

export function AppContentInnerWrapper({
  isNavigationBarOpen,
  isMobile,
  isFullScreen,
  coquille = false,
  children,
}: AppContentInnerWrapperProps) {
  if (isMobile) {
    return children;
  }

  // Sous-plan 10, lot E1 : dans l'iframe de l'app, pas de panneau dans le panneau : le chat a ras.
  // Les enfants gardent h-panel (100dvh moins le rem que prenaient les marges du panneau) : la
  // variable est redefinie ici pour couvrir toute la hauteur ; le fond reste celui du panneau.
  // Maquette de la coquille (01/10) : l'app a retire sa coquille, le rectangle revient ici.
  if (modeIntegre() && !coquille) {
    return (
      <div
        className="flex-1 overflow-hidden bg-panel-background"
        style={
          {
            "--panel-height":
              "calc(100dvh - var(--dev-bar-height) - var(--banner-height))",
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "my-2 mr-2 rounded-xl flex-1 bg-panel-background border border-border overflow-hidden h-panel",
        !isNavigationBarOpen && !isFullScreen && "ml-5",
        isFullScreen && "ml-2"
      )}
      style={{
        boxShadow:
          "0 0 0 0.4px rgba(0, 0, 0, 0.02), 0 0 1px 1px rgba(0, 0, 0, 0.02)",
      }}
    >
      {children}
    </div>
  );
}

export function AppContentLayout({ children }: AppContentLayoutProps) {
  const owner = useWorkspace();
  const isMobile = useIsMobile();
  const { featureFlags, subscription, user } = useAuth();
  const {
    contentClassName,
    contentWidth,
    hasTitle = false,
    hideSidebar = false,
    navChildren,
    pageTitle,
    subNavigation,
    title,
  } = useAppLayout();
  const [fullScreenHash] = useHashParam(FULL_SCREEN_HASH_PARAM);
  const coquille = useNavigationDeLApp()?.coquille === true;

  const isFullScreen = fullScreenHash === "true";

  const hasTitleBar = !!title || hasTitle;
  useDocumentTitle(pageTitle || `${PRODUCT_NAME} - ${owner.name}`);
  useAppKeyboardShortcuts(owner);
  const { isNavigationBarOpen, setIsNavigationBarOpen } =
    useDesktopNavigation();

  useDocumentScrollMode(isMobile);

  const isCenteredContent = contentWidth === "centered";
  const hasTitleBarOffsetHeight = hasTitleBar && !!title && isCenteredContent;

  return (
    <div
      className={cn(
        "flex flex-col",
        isMobile ? MOBILE_DOCUMENT_SCROLL_CLASSES.contentRoot : "h-dvh"
      )}
    >
      <TopBanners owner={owner} subscription={subscription} />
      <div
        className={cn(
          "flex flex-row",
          isMobile
            ? MOBILE_DOCUMENT_SCROLL_CLASSES.contentRow
            : "min-h-0 flex-1"
        )}
      >
        <Navigation
          hideSidebar={hideSidebar}
          isNavigationBarOpen={isNavigationBarOpen}
          setNavigationBarOpen={setIsNavigationBarOpen}
          owner={owner}
          subscription={subscription}
          navChildren={navChildren}
          subNavigation={subNavigation}
          isFullScreen={isFullScreen}
          isMobile={isMobile}
        />
        <div
          className={cn(
            "relative flex w-full flex-1 flex-col text-foreground",
            isMobile
              ? MOBILE_DOCUMENT_SCROLL_CLASSES.contentMain
              : "h-full overflow-x-hidden bg-app-background"
          )}
        >
          <AppContentInnerWrapper
            // Sous-plan 10 : en mode integre la colonne de navigation reste ouverte (Navigation.tsx),
            // le cadre du contenu doit suivre, sinon une marge gauche en trop si le rail avait ete replie.
            isNavigationBarOpen={isNavigationBarOpen || modeIntegre()}
            isMobile={isMobile}
            isFullScreen={isFullScreen}
            coquille={coquille}
          >
            <div
              className={cn(
                "flex flex-1 flex-col",
                isMobile
                  ? MOBILE_DOCUMENT_SCROLL_CLASSES.contentArea
                  : cn(
                      "min-h-0 overflow-y-auto [scrollbar-gutter:stable]",
                      !hasTitleBar && "h-panel"
                    )
              )}
            >
              {/* Temporary measure to preserve title existence on smaller screens.
               * Page has no title, prepend empty AppLayoutTitle. */}
              {!hasTitleBar ? <AppLayoutTitle /> : null}
              {hasTitleBar && contentWidth ? title : null}
              <div
                className={
                  contentWidth
                    ? cn(
                        "flex w-full flex-col items-center",
                        isMobile
                          ? MOBILE_DOCUMENT_SCROLL_CLASSES.contentArea
                          : "overflow-y-auto [scrollbar-gutter:stable]",
                        hasTitleBarOffsetHeight && "h-[calc(100vh-3.5rem)]",
                        !hasTitleBarOffsetHeight &&
                          (hasTitleBar || !isMobile) &&
                          "h-full",
                        isCenteredContent ? "pt-4" : "pt-8",
                        contentClassName
                      )
                    : "contents"
                }
              >
                <div
                  className={
                    contentWidth
                      ? cn(
                          "flex w-full grow flex-col px-4 md:px-8",
                          isCenteredContent && "max-w-4xl"
                        )
                      : "contents"
                  }
                >
                  {children}
                </div>
              </div>
            </div>
          </AppContentInnerWrapper>
        </div>
        <CommandPalette key={owner.sId} owner={owner} user={user} />
      </div>
      {DevFeatureFlagPanel && (
        <Suspense fallback={null}>
          <DevFeatureFlagPanel serverFlags={featureFlags} />
        </Suspense>
      )}
    </div>
  );
}
